import React, { Fragment, useEffect, useState } from 'react';
import { Box, Flex, Image, Skeleton, Spinner, Text } from '@chakra-ui/react';
import { t } from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from 'slices/app.slice';
import icon404ImageNotFound from '../../assets/images/404-image-not-found.svg';
import moment from 'moment/moment';

/**
 * @typedef {Object} ScanHistory
 * @property {string[]} available_at
 * @property {string} scan_id
 * @property {string} barcode
 * @property {string} environment
 * @property {string} event_name
 * @property {string} event_type
 * @property {string} found_at
 * @property {boolean} is_found
 * @property {string} main_app_version
 * @property {number} product_id
 * @property {string} product_name
 * @property {string} product_image_url
 * @property {string} product_url
 * @property {Timestamp} timestamp
 * @property {string} user_email
 * @property {string} user_uid
 * @property {function(): Promise<void>} openProductAnalysisPage
 */

/**
 * Data obtained from the Redux store.
 * @typedef {Object} ReduxData
 * @property {ScanHistory[]} scanHistoryList - An array of scan history data.
 * @property {boolean} isLoadScanHistory - A boolean indicating whether scan history data is fetching.
 * @property {boolean} isScanHistoryHasMoreData
 */

/**
 * Scan History Card
 * @param {{
 *  scanHistory: ScanHistory|null
 * }}
 * @returns
 */
const ScanHistoryCard = ({ scanHistory = null }) => {
  const dispatch = useDispatch();
  const [imageURL, setImageURL] = useState(
    scanHistory?.product_image_url ?? icon404ImageNotFound
  );

  /**
   * @returns {string}
   */
  const renderTimestamp = () => {
    if (scanHistory === null || scanHistory?.timestamp === null) {
      return '-';
    }

    if (moment().isSame(scanHistory.timestamp.toDate(), 'date')) {
      return `Today, ${moment(scanHistory.timestamp.toDate()).format('HH:mm')}`;
    }

    return moment(scanHistory.timestamp.toDate()).format('DD MMM[,] HH:mm');
  };

  const viewHistory = async () => {
    if (scanHistory !== null && scanHistory.is_found) {
      dispatch(actions.setIsShowOverlayLoader(true));
      await scanHistory.openProductAnalysisPage();
      dispatch(actions.setIsShowOverlayLoader(false));
    }
  };

  const productName =
    scanHistory === null
      ? ''
      : !scanHistory.is_found
      ? 'Product Not Found'
      : scanHistory.product_name === ''
      ? 'N/A'
      : scanHistory.product_name;

  return (
    <Flex
      w="full"
      alignItems="center"
      backgroundColor={scanHistory?.is_found === false ? '#ebe7e3' : '#F8F9FB'}
      boxShadow="sm"
      borderRadius="12px"
      minH="75px"
      position="relative"
      p="4"
      cursor="pointer"
      onClick={viewHistory}
      gap="3"
    >
      <Box w="48px" h="full">
        <Image
          objectPosition="center"
          objectFit="contain"
          w="full"
          h="full"
          src={imageURL}
          fallbackSrc={icon404ImageNotFound}
          onError={(e) => setImageURL(icon404ImageNotFound)}
          alt={scanHistory?.product_name}
          fallback={
            scanHistory !== null &&
            scanHistory.is_found && (
              <Flex
                w="full"
                h="full"
                justifyContent="center"
                alignItems="center"
                bg="#ddd"
              >
                <Spinner
                  thickness="7px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="teal.500"
                  size="xl"
                />
              </Flex>
            )
          }
          sx={{
            mixBlendMode: 'multiply',
          }}
        />
      </Box>

      <Flex flex="1" minW="0">
        <Box flex="1" minW="0" overflow="hidden">
          <Text
            fontSize={{ base: '14px', md: '16px' }}
            fontWeight="600"
            noOfLines={1}
            title={productName}
          >
            {productName}
          </Text>

          <Text fontSize={{ base: '12px', md: '14px' }}>
            {renderTimestamp()}
          </Text>
        </Box>
      </Flex>

      <Box textAlign="right" ml="4">
        <Text fontSize={{ base: '12px', md: '14px' }} mb="1">
          {t('history.product-scans.scan-id')}
        </Text>

        <Text fontSize={{ base: '12px', md: '14px' }} fontWeight="600">
          {scanHistory?.scan_id || '-'}
        </Text>
      </Box>
    </Flex>
  );
};

/**
 * Scan History Skeleton
 * @param {{
 *  length: number
 * }} param0
 */
const ScanBoxSkeletons = ({ length = 10 }) => {
  return (
    <Fragment>
      {Array.from({ length: length }, () => 0).map((_, idx) => (
        <Skeleton isLoaded={false} key={idx} rounded="12px">
          <ScanHistoryCard scanHistory={null} />
        </Skeleton>
      ))}
    </Fragment>
  );
};

export const ProductScanHistoryList = () => {
  const dispatch = useDispatch();

  /**
   * @type {ReduxData}
   */
  const { scanHistoryList, isLoadScanHistory, isScanHistoryHasMoreData } =
    useSelector((state) => state.app);

  useEffect(() => {
    if (
      scanHistoryList.length === 0 &&
      !isLoadScanHistory &&
      isScanHistoryHasMoreData
    ) {
      dispatch(actions.loadScanHistoryList(10, null));
    }
  }, [dispatch, scanHistoryList, isLoadScanHistory, isScanHistoryHasMoreData]);

  useEffect(() => {
    const handleWindowScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop <
          document.documentElement.offsetHeight - 5 ||
        isLoadScanHistory ||
        !isScanHistoryHasMoreData
      ) {
        return;
      }

      if (scanHistoryList.length !== 0 && isScanHistoryHasMoreData) {
        dispatch(
          actions.loadScanHistoryList(
            10,
            scanHistoryList[scanHistoryList.length - 1].timestamp
          )
        );
      }
    };
    window.addEventListener('scroll', handleWindowScroll);
    return () => window.removeEventListener('scroll', handleWindowScroll);
  }, [isLoadScanHistory, dispatch, scanHistoryList, isScanHistoryHasMoreData]);

  return (
    <Fragment>
      <ScanHistoryWarningBox />

      <Flex flexDir="column" mt="4" rowGap={'16px'} width="full" height="full">
        {isLoadScanHistory && scanHistoryList.length === 0 ? (
          <ScanBoxSkeletons length={8} />
        ) : (
          scanHistoryList.map((v, i) => {
            return (
              <ScanHistoryCard key={`${i}-product-scan`} scanHistory={v} />
            );
          })
        )}
        {!isLoadScanHistory && scanHistoryList.length === 0 && (
          <Box>
            <Text fontSize={{ base: '14px', md: '16px' }}>
              {t('history.product-scans.your-scan-history-empty')}
            </Text>
          </Box>
        )}
        {!isScanHistoryHasMoreData && scanHistoryList.length !== 0 ? (
          <Flex w={'full'} justifyContent={'center'}>
            <Text fontSize={'10px'}>
              {t('history.product-scans.no-more-scan-history')}
            </Text>
          </Flex>
        ) : null}
        <Skeleton
          visibility={
            isLoadScanHistory && scanHistoryList.length !== 0
              ? 'visible'
              : 'hidden'
          }
        >
          <Box height={'80px'} width={'full'} />
        </Skeleton>
      </Flex>
    </Fragment>
  );
};

/**
 * Temporary hidden until payment feature developed
 */
export const ScanHistoryWarningBox = () => {
  return (
    <Box width="full" p="4" bgColor="#D9D9D9" display={'none'}>
      <Text
        color="black"
        fontWeight="500"
        fontSize={['xl', 'md', 'md', 'large']}
        textAlign="center"
      >
        You have 3 Days left in your free trial.
        <br />
        Click here to get more unlimited scans
      </Text>
    </Box>
  );
};
